import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getRankingService } from '../../../domains/ranking';

const isResultOk = (res: any) => res?.result_code === '200' || res?.resultCode === 200;
const dataOf = (res: any) => (isResultOk(res) ? res?.data ?? null : null);

type Scope = 'national' | 'region' | 'pharmacy_rate';

// GET /insight/product-ranking — data: { scope_label, context, total_count, period, items }
export const useProductRanking = ({ scope, limit, page = 1 }: { scope?: Scope; limit: number; page?: number }) =>
  useQuery({
    queryKey: ['insight-product-ranking', scope, page, limit],
    queryFn: async () => dataOf(await getRankingService().getProductRanking({ scope, page, limit })),
    enabled: !!scope,
  });

// GET /insight/product-trend/{product_id} — data: { product_name, series: [{ date, national_qty, my_qty, national_rank }] }
export const useProductTrend = (productId?: number | string, scope?: Scope) =>
  useQuery({
    queryKey: ['insight-product-trend', productId, scope],
    queryFn: async () =>
      dataOf(await getRankingService().getProductTrend(productId, { scope, interval: 'week' })),
    enabled: productId != null && !!scope,
  });

// GET /insight/highlights — data: { available, period, items: [{ type:'surge'|'drop', label, product_id, name, national_rank, rank_delta }] }
export const useHighlights = () =>
  useQuery({
    queryKey: ['insight-highlights'],
    queryFn: async () => dataOf(await getRankingService().getHighlights()),
  });

// GET /insight/product-detail/{product_id} — data: { health_info: { summary, dosage|null, target_tags } }
export const useProductDetail = (productId?: string) =>
  useQuery({
    queryKey: ['insight-product-detail', productId],
    queryFn: async () => dataOf(await getRankingService().getProductDetail(productId)),
    enabled: !!productId,
  });

// GET /insight/search-trend — available=false 면 화면에서 블록 자체를 그리지 않음
export const useSearchTrend = (barcode?: string) =>
  useQuery({
    queryKey: ['insight-search-trend', barcode],
    queryFn: async () => dataOf(await getRankingService().getSearchTrend({ barcode, weeks: 8 })),
    enabled: !!barcode,
  });

// GET /insight/my-products — { total_count, has_next, list: [{ barcode, name, manufacturer, product_type, sales_qty_30d, last_sold_at }] }
// 단가는 없음(POS 판매 기록에 가격 없음). 여기 나오는 상품은 판매 이력 기준 재고 있음(in_stock)
export const useMyProducts = (q: string) =>
  useQuery({
    queryKey: ['insight-my-products', q],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await getRankingService().getMyProducts({ q: q || undefined, page: 1, limit: 50 });
      return isResultOk(res) ? res : null;
    },
  });
